import { useCallback, useMemo, useState } from "react";
import { Tasks } from "./Tasks";
import { Trades } from "./Trades";
import { Invite } from "./Invite";
import { Swipeable, SwipeDirection } from "@src/components";
import { RewardsState, useRewards } from "@src/contexts";

const enum RewardsTab {
  Tasks = "tasks",
  Trades = "trades",
  Invite = "invite",
}
const rewardsTabOrder = [
  RewardsTab.Tasks,
  RewardsTab.Trades,
  RewardsTab.Invite,
];

export const RewardsTabsLayout = () => {
  const [activeTab, setActiveTab] = useState<RewardsTab>(RewardsTab.Tasks);
  const rewardsData = useRewards();
  const { tasks, claimTask, points, loadingState, referralCode, verifyTask } =
    rewardsData;

  const onSwipe = (direction: SwipeDirection) => {
    const currentIndex = rewardsTabOrder.indexOf(activeTab);
    let nextIndex = currentIndex;
    if (direction === "left") {
      nextIndex = Math.min(currentIndex + 1, rewardsTabOrder.length - 1);
    } else if (direction === "right") {
      nextIndex = Math.max(currentIndex - 1, 0);
    }
    setActiveTab(rewardsTabOrder[nextIndex]!);
  };

  const getRewardsTabs = useCallback(
    ({
      tasks,
      points,
      referralCode,
      claimTask,
      loadingState,
      verifyTask,
    }: {
      tasks: RewardsState["tasks"];
      points: RewardsState["points"];
      referralCode: RewardsState["referralCode"];
      claimTask: RewardsState["claimTask"];
      loadingState: RewardsState["loadingState"];
      verifyTask: RewardsState["verifyTask"];
    }) => ({
      [RewardsTab.Tasks]: {
        label: "Tasks",
        component: (
          <Tasks
            headerData={{
              completedTasks: {
                curr: points.stats.tasksClaimed,
                prev: points.prevStats.tasksClaimed,
              },
              points: {
                curr: points.stats.taskPoints,
                prev: points.prevStats.taskPoints,
              },
            }}
            tasks={tasks}
            claimTask={claimTask}
            isLoading={loadingState.tasks}
            referralCode={referralCode}
            verifyTask={verifyTask}
          />
        ),
      },
      [RewardsTab.Trades]: {
        label: "Trades",
        component: (
          <Trades
            headerData={{
              volume: {
                curr: points.stats.volume,
                prev: points.prevStats.volume,
              },
              points: {
                curr: points.stats.volumePoints,
                prev: 0,
              },
            }}
          />
        ),
      },
      [RewardsTab.Invite]: {
        label: "Invite Friends",
        component: (
          <Invite
            headerData={{
              invitedFriends: {
                curr: points.stats.referrals,
                prev: points.prevStats.referrals,
              },
              points: {
                curr: points.stats.referralPoints,
                prev: points.prevStats.referralPoints,
              },
            }}
            invitesUnlocked={points.stats.volumePoints > 0}
            referralCode={referralCode}
          />
        ),
      },
    }),
    [],
  );

  const rewardsTabs = useMemo(
    () =>
      getRewardsTabs({
        tasks,
        claimTask,
        points,
        loadingState,
        referralCode,
        verifyTask,
      }),
    [
      tasks,
      claimTask,
      loadingState,
      points,
      referralCode,
      verifyTask,
      getRewardsTabs,
    ],
  );

  return (
    <Swipeable onSwipe={onSwipe}>
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden gap-2 items-center">
        <div className="flex flex-col gap-2 px-4 w-full">
          <div className="w-full flex justify-between gap-2">
            {Object.entries(rewardsTabs).map(([key, { label }]) => (
              <button
                key={key}
                className={`flex-1 flex items-center justify-center text-text_primary/40 text-sm font-medium ${
                  activeTab === key && "!text-text_primary"
                }`}
                onClick={() => {
                  setActiveTab(key as RewardsTab);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="w-full h-[1px] bg-text_primary/10" />
        </div>
        <div className={`flex-1 min-h-0 overflow-y-auto w-full`}>
          {rewardsTabs[activeTab].component}
        </div>
      </div>
    </Swipeable>
  );
};
